import { useRef, useState, useCallback } from "react";

/**
 * Draggable before/after image comparison slider.
 * "before" is grayscale (the old state), "after" reveals the new build.
 */
export default function BeforeAfter({ before, after, className = "" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    update(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    update(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onUp = () => (dragging.current = false);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden border border-white/10 select-none cursor-ew-resize ${className}`}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      data-testid="before-after"
    >
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt="Before"
          className="h-full object-cover grayscale"
          style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%", maxWidth: "none" }}
          draggable={false}
        />
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase bg-black/70 px-3 py-1.5 text-white/80">Before</span>
      </div>
      <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase bg-[color:var(--accent)] px-3 py-1.5 text-white">After</span>
      {/* handle */}
      <div className="absolute top-0 bottom-0 w-px bg-white pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white flex items-center justify-center">
          <span className="font-mono text-black text-xs">↔</span>
        </div>
      </div>
    </div>
  );
}
